/* use-current-step — the encounter's active step, kept inside the nav it is shown in.
 *
 * ── THE DEFECT THIS EXISTS FOR (Ahmed, 2026-09-18) ────────────────────────
 * The active step used to be a plain `useState` whose lazy initialiser read the
 * resolved nav. That initialiser runs ONCE, on the first render — and on that
 * render the encounter template is still in flight, so the author-removed steps
 * (`hiddenSteps`, from the questionnaire's `cps-layout` extension) are not known
 * yet and the nav resolves as if nothing were hidden. The step therefore
 * initialised to `'hpi'` whatever the template said, and nothing recomputed it
 * when the template landed: an encounter sitting on HPI, with no HPI tab in the
 * nav to highlight and no HPI content to render.
 *
 * The ROLE gate never hit this because the role is synchronous. The template is
 * not, and that is the whole difference.
 *
 * ── WHY RECONCILE RATHER THAN DERIVE ──────────────────────────────────────
 * The active step is genuinely state — the clinician moves it. It cannot be
 * derived from the nav. So it stays state, and the nav CORRECTS it, in one
 * direction only: this hook moves off a step that is not in the nav, and never
 * moves a clinician who is standing on a visible step. A refetch that returns
 * the same nav is therefore inert.
 */
import { useEffect, useState } from 'react';

import { type PaStepKey } from './encounter-steps';

/**
 * The active step, and the raw setter for it.
 *
 * `navSteps` is the resolved centre nav (`resolveSteps`) — role gating and the
 * template's `hiddenSteps` already applied. An EMPTY nav leaves the step alone:
 * it is the "still loading" shape, and clamping to a fallback there would be the
 * very bug this hook exists to fix, one layer down.
 *
 * The setter is deliberately the RAW one. Callers that should also mark the step
 * being left as completed go through the page's own `handleStepChange`; a step
 * the clinician could never open must never be ticked as work they did
 * (PROD-1549 gives the same reasoning for the "Start Appointment and Go" CTA).
 */
export function useCurrentStep(
  navSteps: readonly PaStepKey[],
): [PaStepKey, React.Dispatch<React.SetStateAction<PaStepKey>>] {
  const [currentStep, setCurrentStep] = useState<PaStepKey>(() => navSteps[0] ?? 'hpi');

  useEffect(() => {
    if (navSteps.length === 0) return;
    if (!navSteps.includes(currentStep)) setCurrentStep(navSteps[0]);
  }, [navSteps, currentStep]);

  return [currentStep, setCurrentStep];
}
